import { z } from "zod";
import {
  COMMUNITY_ADMIN_PAGE_SIZE,
  communityAdminIdentitySchema,
  communityAdminProjectSchema,
  communityAdminProjectReviewSchema,
  communityAdminProjectCountsSchema,
  communityAdminProjectsQuerySchema,
  communityAdminVerifyRequestSchema,
} from "./community-admin-v1";

export const PANEL_MIN_VOTES = 3;
const dateTimeSchema = z.string().datetime({ offset: true });
const reasonSchema = z.string().trim().min(3).max(500);
export const communityReviewVoteSchema = z.enum(["approve", "decline"]);
export const communityReviewSessionSchema = z.object({
  identity: communityAdminIdentitySchema,
  expiresAt: dateTimeSchema,
  scope: z.literal("review"),
  override: z.boolean(),
}).strict();
export const communityReviewSessionResponseSchema = communityReviewSessionSchema.extend({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export const communityReviewVerifyRequestSchema = communityAdminVerifyRequestSchema;
export const communityReviewPanelTallySchema = z.object({
  approve: z.number().int().nonnegative(),
  decline: z.number().int().nonnegative(),
  quorum: z.number().int().positive(),
  myVote: communityReviewVoteSchema.nullable(),
}).strict();
export const communityReviewProjectSchema = communityAdminProjectSchema.extend({ panel: communityReviewPanelTallySchema });
export const communityReviewProjectsPageSchema = z.object({
  items: z.array(communityReviewProjectSchema).max(COMMUNITY_ADMIN_PAGE_SIZE),
  nextCursor: z.string().nullable(),
}).strict();
export const communityReviewProjectReviewSchema = communityAdminProjectReviewSchema;
export const communityReviewProjectCountsSchema = communityAdminProjectCountsSchema;
export const communityReviewProjectsQuerySchema = communityAdminProjectsQuerySchema;
export const communityReviewVoteRequestSchema = z.object({
  vote: communityReviewVoteSchema,
  updatedAt: dateTimeSchema,
  reason: reasonSchema.optional(),
}).strict().refine((value) => value.vote !== "decline" || value.reason !== undefined, {
  message: "Declining requires a reason", path: ["reason"],
});
export const communityReviewDecideRequestSchema = z.object({
  action: communityReviewVoteSchema,
  updatedAt: dateTimeSchema,
  reason: reasonSchema.optional(),
}).strict().refine((value) => value.action !== "decline" || value.reason !== undefined, {
  message: "Declining requires a reason", path: ["reason"],
});
export const communityReviewPanelMemberSchema = z.object({
  personId: z.string().uuid(),
  xUsername: z.string().max(64).nullable(),
  xName: z.string().max(128).nullable(),
  referralCode: z.string().min(1).max(16),
  addedBy: z.string().min(1).max(320),
  addedAt: dateTimeSchema,
  note: z.string().max(200).nullable(),
}).strict();
export const communityReviewPanelMembersSchema = z.object({ members: z.array(communityReviewPanelMemberSchema) }).strict();
export const communityReviewPanelAddRequestSchema = z.object({
  personId: z.string().uuid(), note: z.string().trim().max(200).optional(),
}).strict();
export const communityReviewPanelMutationResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type CommunityReviewSession = z.infer<typeof communityReviewSessionSchema>;
export type CommunityReviewSessionResponse = z.infer<typeof communityReviewSessionResponseSchema>;
export type CommunityReviewPanelTally = z.infer<typeof communityReviewPanelTallySchema>;
export type CommunityReviewProject = z.infer<typeof communityReviewProjectSchema>;
export type CommunityReviewProjectsPage = z.infer<typeof communityReviewProjectsPageSchema>;
export type CommunityReviewVoteRequest = z.infer<typeof communityReviewVoteRequestSchema>;
export type CommunityReviewDecideRequest = z.infer<typeof communityReviewDecideRequestSchema>;
export type CommunityReviewPanelMember = z.infer<typeof communityReviewPanelMemberSchema>;
export type CommunityReviewPanelMembers = z.infer<typeof communityReviewPanelMembersSchema>;
export type CommunityReviewPanelAddRequest = z.infer<typeof communityReviewPanelAddRequestSchema>;
